import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  Check,
  Copy,
  Eye,
  Gift,
  Loader2,
  Lock,
  Mic,
  Minus,
  Monitor,
  Play,
  Plus,
  Radio,
  ShieldCheck,
  Sparkles,
  Trash2,
  Trophy,
  Users,
  Video,
  X,
} from 'lucide-react';
import {
  gameshowApi,
  GameShowApiError,
  type Delivery,
  type GateMode,
  type HostContestant,
  type HostMatch,
  type HostQuestion,
  type HostResponse,
  type QuestionDraft,
  type ShowStatus,
  type Sponsorship,
  type Tape,
} from '@/services/gameshowApi';
import { useToast } from '@/hooks/use-toast';

// ── /show-host/:showId ────────────────────────────────────────────────
// Host control panel. Three regions (per the Host comp): the run-of-show
// queue, the live control for the current question (Open → Lock → Reveal),
// and the leaderboard. Inline question authoring lives in the center panel.
// Optimistic-free: every action hits the endpoint, then refetches.

const PAGE_BG = 'radial-gradient(120% 60% at 30% -10%, #241b30 0%, #16110f 55%, #0c0908 100%)';
const GOLD = '#ffd600';
const GREEN = '#8cc63f';
const PINK = '#f286c9';
const REC = '#ff4d6a';
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

type Draft = {
  key: string;
  _id?: string;
  text: string;
  options: string[];
  correctOption: number | null;
  points: number;
  decaySeconds: number;
  delivery: Delivery;
  isGate: boolean;
};

let draftSeq = 0;
const newDraftKey = () => `d_${Date.now()}_${draftSeq++}`;

const buildDrafts = (questions: HostQuestion[]): Draft[] =>
  questions
    .filter((q) => q.state === 'queued')
    .sort((a, b) => a.order - b.order)
    .map((q) => ({
      key: q._id,
      _id: q._id,
      text: q.text,
      options: q.options.length ? [...q.options] : ['', ''],
      correctOption: q.correctOption,
      points: q.points,
      decaySeconds: q.decaySeconds,
      delivery: q.isGate ? 'host_read' : q.delivery,
      isGate: q.isGate,
    }));

const emptyDraft = (): Draft => ({
  key: newDraftKey(),
  text: '',
  options: ['', ''],
  correctOption: 0,
  points: 1000,
  decaySeconds: 15,
  delivery: 'host_read',
  isGate: false,
});

const kicker: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  fontWeight: 600,
};

const stateChip: Record<HostQuestion['state'], { label: string; color: string }> = {
  queued: { label: 'Queued', color: 'rgba(255,255,255,.45)' },
  open: { label: 'Live', color: REC },
  locked: { label: 'Locked', color: GOLD },
  revealed: { label: 'Revealed', color: GREEN },
};

const GameShowHostPage: React.FC = () => {
  const { showId } = useParams<{ showId: string }>();
  const { toast } = useToast();

  const [data, setData] = useState<HostResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [selKey, setSelKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false); // an open/lock/reveal/status call in flight
  // Come On Down (stage draw) controls.
  const [drawCount, setDrawCount] = useState(3);
  const [drawing, setDrawing] = useState(false);
  const [drawError, setDrawError] = useState<string | null>(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  // Video Battle: ordered tape selection for casting the bracket.
  const [tapeSel, setTapeSel] = useState<string[]>([]);
  const [castBusy, setCastBusy] = useState(false);
  const [matchBusy, setMatchBusy] = useState(false);

  const setDirtyBoth = (v: boolean) => {
    dirtyRef.current = v;
    setDirty(v);
  };

  const load = useCallback(async () => {
    if (!showId) return;
    try {
      const resp = await gameshowApi.host(showId);
      setData(resp);
      setError(null);
      if (!dirtyRef.current) setDrafts(buildDrafts(resp.questions));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the show.');
    }
  }, [showId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll every 3s while the show is running (lobby/live). Stop when ended.
  const status = data?.show.status;
  useEffect(() => {
    if (status !== 'lobby' && status !== 'live') return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [status, load]);

  const questions = data?.questions ?? [];
  const liveQuestions = questions.filter((q) => q.state !== 'queued');
  const activeLive =
    questions.find((q) => q.state === 'open') ||
    questions.find((q) => q.state === 'locked') ||
    [...questions].filter((q) => q.state === 'revealed').sort((a, b) => b.order - a.order)[0] ||
    null;

  // Resolve the current selection → a draft (editable) or a live question.
  const selDraft = drafts.find((d) => d.key === selKey) || null;
  const selLive = !selDraft ? liveQuestions.find((q) => q._id === selKey) || null : null;

  // Default selection when nothing valid is picked.
  useEffect(() => {
    if (selDraft || selLive) return;
    if (activeLive) setSelKey(activeLive._id);
    else if (drafts.length) setSelKey(drafts[0].key);
    else setSelKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selDraft, selLive, activeLive?._id, drafts.length]);

  // ── draft mutations ──
  const patchDraft = (key: string, patch: Partial<Draft>) => {
    setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)));
    setDirtyBoth(true);
  };

  const addQuestion = () => {
    const d = emptyDraft();
    setDrafts((ds) => [...ds, d]);
    setSelKey(d.key);
    setDirtyBoth(true);
  };

  const deleteDraft = (key: string) => {
    setDrafts((ds) => ds.filter((d) => d.key !== key));
    setDirtyBoth(true);
    if (selKey === key) setSelKey(null);
  };

  const moveDraft = (key: string, dir: -1 | 1) => {
    setDrafts((ds) => {
      const i = ds.findIndex((d) => d.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ds.length) return ds;
      const copy = [...ds];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
    setDirtyBoth(true);
  };

  const draftValid = (d: Draft): string | null => {
    if (!d.text.trim()) return 'Question text is required.';
    const opts = d.options.map((o) => o.trim());
    if (opts.length < 2 || opts.length > 4) return 'Each question needs 2–4 options.';
    if (opts.some((o) => !o)) return 'Every option needs a label.';
    if (d.correctOption != null && (d.correctOption < 0 || d.correctOption >= opts.length))
      return 'Pick which option is correct.';
    return null;
  };

  const saveQueue = async () => {
    if (!showId) return;
    for (const d of drafts) {
      const err = draftValid(d);
      if (err) {
        setSelKey(d.key);
        toast({ title: 'Fix before saving', description: err, variant: 'destructive' });
        return;
      }
    }
    setSaving(true);
    try {
      const payload: QuestionDraft[] = drafts.map((d) => ({
        text: d.text.trim(),
        options: d.options.map((o) => o.trim()),
        correctOption: d.correctOption,
        points: d.points,
        decaySeconds: d.decaySeconds,
        delivery: d.isGate ? 'host_read' : d.delivery,
        isGate: d.isGate,
      }));
      const resp = await gameshowApi.saveQuestions(showId, payload);
      setData(resp);
      setDrafts(buildDrafts(resp.questions));
      setDirtyBoth(false);
      toast({ title: 'Queue saved' });
    } catch (e) {
      if (e instanceof GameShowApiError && e.status === 409) {
        toast({
          title: 'Queue changed elsewhere',
          description: 'Reloading the latest version.',
          variant: 'destructive',
        });
        setDirtyBoth(false);
        load();
      } else {
        toast({
          title: 'Could not save',
          description: e instanceof Error ? e.message : undefined,
          variant: 'destructive',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // ── live actions (call → refetch) ──
  const runAction = async (fn: () => Promise<unknown>, selectAfter?: string) => {
    setBusy(true);
    try {
      await fn();
      await load();
      if (selectAfter !== undefined) setSelKey(selectAfter);
    } catch (e) {
      toast({
        title: 'Action failed',
        description: e instanceof Error ? e.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const openQuestion = (qid: string) => runAction(() => gameshowApi.openQuestion(showId!, qid), qid);
  const lockQuestion = (qid: string) => runAction(() => gameshowApi.lockQuestion(showId!, qid), qid);
  const revealQuestion = (qid: string) => runAction(() => gameshowApi.revealQuestion(showId!, qid), qid);
  const setStatus = (s: ShowStatus) => runAction(() => gameshowApi.setStatus(showId!, s));

  // ── Run lifecycle ──
  const newGame = () => {
    if (!window.confirm("Save this game's scores and start a fresh game? The board resets to the lobby.")) return;
    runAction(() => gameshowApi.newGame(showId!));
  };
  const exportScores = async () => {
    try {
      await gameshowApi.exportScores(showId!);
    } catch (e) {
      toast({ title: 'Export failed', description: e instanceof Error ? e.message : undefined, variant: 'destructive' });
    }
  };

  const selectNextQueued = () => {
    if (drafts.length) setSelKey(drafts[0].key);
  };

  // ── Come On Down: draw / eject / gate settings ──
  const doDraw = async () => {
    if (!showId || drawing) return;
    setDrawing(true);
    setDrawError(null);
    try {
      const res = await gameshowApi.draw(showId, drawCount);
      await load();
      toast({ title: `Called down ${res.drawn.length} contestant${res.drawn.length === 1 ? '' : 's'}` });
    } catch (e) {
      if (e instanceof GameShowApiError && e.status === 409) {
        const ec = typeof e.body?.eligibleCount === 'number' ? (e.body.eligibleCount as number) : 0;
        setDrawError(
          `Only ${ec} eligible ${ec === 1 ? 'player is' : 'players are'} in the pool — you asked for ${drawCount}. Have more viewers opt in, or lower the count.`
        );
      } else {
        toast({ title: 'Draw failed', description: e instanceof Error ? e.message : undefined, variant: 'destructive' });
      }
    } finally {
      setDrawing(false);
    }
  };

  const ejectContestant = async (userId: string) => {
    if (!showId) return;
    try {
      await gameshowApi.ejectContestant(showId, userId);
      await load();
    } catch (e) {
      toast({ title: 'Could not eject', description: e instanceof Error ? e.message : undefined, variant: 'destructive' });
    }
  };

  const patchSettings = async (patch: { gateMode?: GateMode; topN?: number }) => {
    if (!showId || settingsBusy) return;
    setSettingsBusy(true);
    try {
      await gameshowApi.updateSettings(showId, patch);
      await load();
    } catch (e) {
      toast({ title: 'Could not update', description: e instanceof Error ? e.message : undefined, variant: 'destructive' });
    } finally {
      setSettingsBusy(false);
    }
  };

  // ── Sponsorship: create / void ──
  const [sponsorBusy, setSponsorBusy] = useState(false);
  const createSponsorship = async (offerText: string, amountCents: number) => {
    if (!showId || sponsorBusy) return;
    setSponsorBusy(true);
    try {
      await gameshowApi.createSponsorship(showId, offerText, amountCents);
      await load();
      toast({ title: 'Sponsor link created', description: 'Share it with your sponsor to fund the show.' });
    } catch (e) {
      toast({ title: 'Could not create', description: e instanceof Error ? e.message : undefined, variant: 'destructive' });
    } finally {
      setSponsorBusy(false);
    }
  };
  const voidSponsorship = async (sponsorshipId: string) => {
    if (!showId) return;
    try {
      await gameshowApi.voidSponsorship(sponsorshipId);
      await load();
    } catch (e) {
      toast({ title: 'Could not remove', description: e instanceof Error ? e.message : undefined, variant: 'destructive' });
    }
  };

  // ── Video Battle: cast the bracket + drive matches ──
  const toggleTape = (id: string) =>
    setTapeSel((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

  const castBracket = async () => {
    if (!showId || castBusy || tapeSel.length < 2) return;
    setCastBusy(true);
    try {
      await gameshowApi.generateMatches(showId, tapeSel);
      await load();
      setTapeSel([]);
      toast({ title: 'Bracket cast' });
    } catch (e) {
      toast({ title: 'Could not cast bracket', description: e instanceof Error ? e.message : undefined, variant: 'destructive' });
    } finally {
      setCastBusy(false);
    }
  };

  const runMatch = async (fn: () => Promise<unknown>) => {
    setMatchBusy(true);
    try {
      await fn();
      await load();
    } catch (e) {
      toast({ title: 'Action failed', description: e instanceof Error ? e.message : undefined, variant: 'destructive' });
    } finally {
      setMatchBusy(false);
    }
  };
  const openMatch = (mid: string) => runMatch(() => gameshowApi.openMatch(showId!, mid));
  const lockMatch = (mid: string) => runMatch(() => gameshowApi.lockMatch(showId!, mid));
  const revealMatch = (mid: string) => runMatch(() => gameshowApi.revealMatch(showId!, mid));

  const progressLabel = (() => {
    const total = questions.length;
    if (!total) return 'No questions yet';
    const done = questions.filter((q) => q.state === 'revealed').length;
    return `${done} of ${total} revealed`;
  })();

  // ── render ──
  if (!data && !error) {
    return (
      <Shell>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,.6)', padding: 60 }}>
          <Loader2 size={18} className="animate-spin" /> Loading host panel…
        </div>
      </Shell>
    );
  }
  if (error && !data) {
    return (
      <Shell>
        <div style={{ color: '#ff9db3', fontSize: 14, padding: 60 }}>{error}</div>
      </Shell>
    );
  }

  const show = data!.show;

  return (
    <Shell>
      {/* LIVE BAR */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
          padding: '14px 20px',
          borderBottom: '1px solid rgba(255,255,255,.09)',
          background:
            show.status === 'live'
              ? 'rgba(255,77,106,.06)'
              : show.status === 'paused'
                ? 'rgba(255,214,0,.06)'
                : 'rgba(255,255,255,.02)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...kicker, color: show.status === 'live' ? REC : GOLD }}>
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: show.status === 'live' ? REC : show.status === 'ended' ? 'rgba(255,255,255,.4)' : GOLD,
            }}
          />
          {show.status === 'paused' ? 'Paused' : show.status}
        </div>
        <div className="font-hj-display" style={{ fontSize: 18, color: GOLD }}>
          {show.title}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>
          {show.format === 'battle' ? 'Video battle' : 'Quiz show'} · {progressLabel}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <StatusButton label="Open lobby" active={show.status === 'lobby'} disabled={busy} onClick={() => setStatus('lobby')} />
          <StatusButton label="Go live" active={show.status === 'live'} accent onClick={() => setStatus('live')} disabled={busy} />
          {show.status === 'paused' ? (
            <StatusButton label="Resume" active accent disabled={busy} onClick={() => setStatus('live')} />
          ) : (
            <StatusButton label="Pause" active={false} disabled={busy || show.status !== 'live'} onClick={() => setStatus('paused')} />
          )}
          <StatusButton
            label="End show"
            active={show.status === 'ended'}
            danger
            disabled={busy}
            onClick={() => setStatus('ended')}
          />
          <StatusButton label="New game" active={false} disabled={busy} onClick={newGame} />
          <StatusButton label="Export scores (CSV)" active={false} disabled={busy} onClick={exportScores} />
        </div>
      </div>

      {/* 3-COLUMN BODY */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 280px) 1fr minmax(240px, 300px)' }}>
        {/* LEFT: QUEUE */}
        <div
          style={{
            borderRight: '1px solid rgba(255,255,255,.09)',
            padding: '20px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ ...kicker, letterSpacing: '0.2em', color: 'rgba(255,255,255,.5)' }}>Run of show</span>
            {dirty && (
              <button
                onClick={saveQueue}
                disabled={saving}
                style={{
                  ...kicker,
                  cursor: saving ? 'default' : 'pointer',
                  border: 'none',
                  borderRadius: 8,
                  padding: '5px 10px',
                  color: '#150e20',
                  background: GOLD,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                {saving ? <Loader2 size={11} className="animate-spin" /> : null}
                Save queue
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Live/past questions (read-only) */}
            {liveQuestions
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((q) => (
                <QueueRow
                  key={q._id}
                  n={q.order + 1}
                  text={q.text || `Question ${q.order + 1}`}
                  chip={stateChip[q.state]}
                  delivery={q.delivery}
                  isGate={q.isGate}
                  selected={selKey === q._id}
                  onSelect={() => setSelKey(q._id)}
                />
              ))}

            {/* Editable queued drafts */}
            {drafts.map((d, i) => (
              <QueueRow
                key={d.key}
                n={liveQuestions.length + i + 1}
                text={d.text || 'Untitled question'}
                chip={{ label: d._id ? 'Queued' : 'New', color: d._id ? 'rgba(255,255,255,.45)' : GOLD }}
                delivery={d.isGate ? 'host_read' : d.delivery}
                isGate={d.isGate}
                selected={selKey === d.key}
                onSelect={() => setSelKey(d.key)}
                onUp={i > 0 ? () => moveDraft(d.key, -1) : undefined}
                onDown={i < drafts.length - 1 ? () => moveDraft(d.key, 1) : undefined}
                onDelete={() => deleteDraft(d.key)}
              />
            ))}
          </div>

          <button
            onClick={addQuestion}
            style={{
              marginTop: 4,
              border: '1px dashed rgba(255,255,255,.22)',
              background: 'transparent',
              color: 'var(--hj-ink-2)',
              fontFamily: 'var(--hj-font-ui)',
              fontWeight: 600,
              fontSize: 13,
              padding: 11,
              borderRadius: 11,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
            }}
          >
            <Plus size={15} /> Add question
          </button>
        </div>

        {/* CENTER: CONTROL / EDITOR */}
        <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {show.format === 'battle' && (
            <TapesBracketCard
              tapes={data!.tapes ?? []}
              matches={data!.matches ?? []}
              bracketFormat={show.settings?.bracketFormat ?? 'single_elim'}
              tapeSel={tapeSel}
              onToggleTape={toggleTape}
              onCast={castBracket}
              castBusy={castBusy}
              matchBusy={matchBusy}
              onOpenMatch={openMatch}
              onLockMatch={lockMatch}
              onRevealMatch={revealMatch}
            />
          )}
          {selLive ? (
            <LiveControl
              q={selLive}
              busy={busy}
              onLock={() => lockQuestion(selLive._id)}
              onReveal={() => revealQuestion(selLive._id)}
              onNext={selectNextQueued}
              hasNext={drafts.length > 0}
            />
          ) : selDraft ? (
            <DraftEditor
              draft={selDraft}
              status={show.status}
              dirty={dirty}
              busy={busy}
              saving={saving}
              onPatch={(patch) => patchDraft(selDraft.key, patch)}
              onSave={saveQueue}
              onOpen={() => selDraft._id && openQuestion(selDraft._id)}
            />
          ) : (
            <div
              style={{
                border: '1px dashed rgba(255,255,255,.16)',
                borderRadius: 16,
                padding: 40,
                textAlign: 'center',
                color: 'rgba(255,255,255,.6)',
                fontSize: 14,
              }}
            >
              No question selected. Add one to start building your run of show.
            </div>
          )}
        </div>

        {/* RIGHT: LEADERBOARD */}
        <div
          style={{
            borderLeft: '1px solid rgba(255,255,255,.09)',
            padding: '20px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <StageCard
            optInCount={data!.optInCount ?? 0}
            contestants={data!.contestants ?? []}
            gateMode={show.settings?.gateMode ?? 'strict'}
            topN={show.settings?.topN ?? 10}
            drawCount={drawCount}
            setDrawCount={setDrawCount}
            drawing={drawing}
            drawError={drawError}
            settingsBusy={settingsBusy}
            onDraw={doDraw}
            onEject={ejectContestant}
            onSetGateMode={(m) => patchSettings({ gateMode: m })}
            onSetTopN={(n) => patchSettings({ topN: n })}
          />

          <SponsorshipCard
            sponsorships={data!.sponsorships ?? []}
            busy={sponsorBusy}
            onCreate={createSponsorship}
            onVoid={voidSponsorship}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Trophy size={15} color={GOLD} />
            <span style={{ ...kicker, letterSpacing: '0.2em', color: 'rgba(255,255,255,.5)' }}>Leaderboard · top 20</span>
          </div>
          {data!.leaderboard.length === 0 ? (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.55)', padding: '12px 0' }}>
              No scores yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {data!.leaderboard.map((r, i) => (
                <div
                  key={`${r.rank}-${r.userName}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    padding: '9px 11px',
                    borderRadius: 10,
                    background: i === 0 ? 'rgba(255,214,0,.1)' : 'rgba(255,255,255,.03)',
                    border: `1px solid ${i === 0 ? 'rgba(255,214,0,.35)' : 'rgba(255,255,255,.07)'}`,
                  }}
                >
                  <span
                    className="font-hj-display tabular"
                    style={{ fontSize: 15, width: 20, color: i === 0 ? GOLD : 'rgba(255,255,255,.65)' }}
                  >
                    {r.rank}
                  </span>
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: 'var(--hj-ink)' }}>{r.userName}</span>
                  <span className="font-hj-display tabular" style={{ fontSize: 15, color: GOLD }}>
                    {r.totalPoints.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div
            style={{
              marginTop: 'auto',
              borderTop: '1px solid rgba(255,255,255,.08)',
              paddingTop: 14,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              fontSize: 11.5,
              lineHeight: 1.5,
              color: 'rgba(255,255,255,.5)',
            }}
          >
            <Video size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            Runs from your laptop. Go live as usual, drive the show from this panel.
          </div>
        </div>
      </div>
    </Shell>
  );
};

// ── Tapes & Bracket (Video Battle) ────────────────────────────────────
// Submitted tapes → multi-select in order → cast the bracket → drive each
// match with the same Open → Lock → Reveal machine as questions.
const TapesBracketCard: React.FC<{
  tapes: Tape[];
  matches: HostMatch[];
  bracketFormat: string;
  tapeSel: string[];
  onToggleTape: (id: string) => void;
  onCast: () => void;
  castBusy: boolean;
  matchBusy: boolean;
  onOpenMatch: (mid: string) => void;
  onLockMatch: (mid: string) => void;
  onRevealMatch: (mid: string) => void;
}> = ({
  tapes,
  matches,
  bracketFormat,
  tapeSel,
  onToggleTape,
  onCast,
  castBusy,
  matchBusy,
  onOpenMatch,
  onLockMatch,
  onRevealMatch,
}) => {
  const formatLabel = bracketFormat === 'round_robin' ? 'Round-robin' : 'Single-elim';
  const anyLiveMatch = matches.some((m) => m.state === 'open' || m.state === 'locked');
  return (
    <div
      style={{
        border: '1px solid rgba(242,134,201,.3)',
        background: 'rgba(242,134,201,.05)',
        borderRadius: 14,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Video size={16} color={PINK} />
        <span className="font-hj-display" style={{ fontSize: 17, color: PINK }}>Tapes & Bracket</span>
        <div style={{ flex: 1 }} />
        <span style={{ ...kicker, fontSize: 9, color: 'rgba(255,255,255,.55)' }}>{formatLabel}</span>
      </div>

      {/* submitted tapes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>
          Submitted tapes · {tapes.length} · tap to build the bracket in order
        </label>
        {tapes.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', padding: '6px 0' }}>
            No tapes yet. Share the submit link with your performers.
          </div>
        ) : (
          tapes.map((t) => {
            const pos = tapeSel.indexOf(t._id);
            const selected = pos >= 0;
            return (
              <div
                key={t._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  border: `1.5px solid ${selected ? 'rgba(242,134,201,.6)' : 'rgba(255,255,255,.09)'}`,
                  background: selected ? 'rgba(242,134,201,.08)' : 'rgba(255,255,255,.02)',
                  borderRadius: 11,
                  padding: '9px 11px',
                }}
              >
                <button
                  onClick={() => onToggleTape(t._id)}
                  title={selected ? 'Remove from bracket' : 'Add to bracket'}
                  style={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    cursor: 'pointer',
                    border: `2px solid ${selected ? PINK : 'rgba(255,255,255,.2)'}`,
                    background: selected ? PINK : 'transparent',
                    color: selected ? '#150e20' : 'rgba(255,255,255,.6)',
                    fontFamily: 'var(--hj-font-ui)',
                    fontWeight: 700,
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {selected ? pos + 1 : ''}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--hj-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.title}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)' }}>
                    {t.userName}
                    {t.status === 'cast' && <span style={{ color: GREEN }}> · cast</span>}
                  </div>
                </div>
                {t.videoUrl && (
                  <a
                    href={t.videoUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ ...kicker, fontSize: 9, color: GOLD, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    <Play size={12} /> Preview
                  </a>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* cast button */}
      <button
        onClick={onCast}
        disabled={castBusy || tapeSel.length < 2}
        title={tapeSel.length < 2 ? 'Select at least two tapes' : undefined}
        style={{
          cursor: castBusy || tapeSel.length < 2 ? 'default' : 'pointer',
          border: 'none',
          borderRadius: 12,
          padding: 13,
          fontFamily: 'var(--hj-font-ui)',
          fontWeight: 700,
          fontSize: 14,
          color: '#150e20',
          background: 'linear-gradient(90deg,#f286c9,#ffd600)',
          opacity: castBusy || tapeSel.length < 2 ? 0.5 : 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        {castBusy ? <Loader2 size={15} className="animate-spin" /> : <Trophy size={15} />}
        {tapeSel.length >= 2 ? `Cast bracket · ${tapeSel.length} tapes` : 'Cast bracket'}
      </button>
      {anyLiveMatch && (
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', lineHeight: 1.4 }}>
          Finish the live matchup before recasting — casting replaces only the queued matches.
        </div>
      )}

      {/* match queue */}
      {matches.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: 12 }}>
          <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>Match queue · {matches.length}</label>
          {matches.map((m) => (
            <MatchRow
              key={m._id}
              m={m}
              busy={matchBusy}
              onOpen={() => onOpenMatch(m._id)}
              onLock={() => onLockMatch(m._id)}
              onReveal={() => onRevealMatch(m._id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// One match: A vs B, live vote counts, and the state-appropriate action button.
const MatchRow: React.FC<{
  m: HostMatch;
  busy: boolean;
  onOpen: () => void;
  onLock: () => void;
  onReveal: () => void;
}> = ({ m, busy, onOpen, onLock, onReveal }) => {
  const total = m.votes.a + m.votes.b;
  const pct = (n: number) => (total ? Math.round((n / total) * 100) : 0);
  const revealed = m.state === 'revealed';
  const winner: 'a' | 'b' | null =
    revealed && total > 0 && m.votes.a !== m.votes.b ? (m.votes.a > m.votes.b ? 'a' : 'b') : null;

  let action: { label: string; icon: React.ReactNode; fn: () => void; bg: string; fg: string } | null = null;
  if (m.state === 'queued') action = { label: 'Open', icon: <Play size={14} />, fn: onOpen, bg: 'linear-gradient(90deg,#ffe566,#ffd600)', fg: '#150e20' };
  else if (m.state === 'open') action = { label: 'Lock votes', icon: <Lock size={14} />, fn: onLock, bg: 'linear-gradient(90deg,#ff4d6a,#f5236a)', fg: '#fff' };
  else if (m.state === 'locked') action = { label: 'Reveal', icon: <Eye size={14} />, fn: onReveal, bg: 'linear-gradient(90deg,#8cc63f,#63a828)', fg: '#08110c' };

  const Side: React.FC<{ side: 'a' | 'b' }> = ({ side }) => {
    const tape = side === 'a' ? m.tapeA : m.tapeB;
    const isWin = winner === side;
    const showCount = m.state === 'locked' || revealed;
    return (
      <div
        style={{
          flex: 1,
          minWidth: 0,
          borderRadius: 9,
          padding: '8px 10px',
          background: isWin ? 'rgba(140,198,63,.1)' : 'rgba(255,255,255,.03)',
          border: `1px solid ${isWin ? 'rgba(140,198,63,.4)' : 'rgba(255,255,255,.08)'}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...kicker, fontSize: 9, color: side === 'a' ? GOLD : PINK }}>{side.toUpperCase()}</span>
          {showCount && (
            <span className="font-hj-display tabular" style={{ fontSize: 13, marginLeft: 'auto', color: isWin ? GREEN : 'var(--hj-ink)' }}>
              {side === 'a' ? m.votes.a : m.votes.b} · {pct(side === 'a' ? m.votes.a : m.votes.b)}%
            </span>
          )}
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tape?.title || 'Missing tape'}
        </div>
        <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tape?.userName || '—'}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        border: '1px solid rgba(255,255,255,.09)',
        background: 'rgba(255,255,255,.02)',
        borderRadius: 12,
        padding: 11,
        display: 'flex',
        flexDirection: 'column',
        gap: 9,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="font-hj-display" style={{ fontSize: 12, color: 'rgba(255,255,255,.55)' }}>
          R{m.round} · M{m.order + 1}
        </span>
        <span style={{ ...kicker, fontSize: 9, color: stateChip[m.state].color }}>{stateChip[m.state].label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
        <Side side="a" />
        <div style={{ display: 'flex', alignItems: 'center', ...kicker, fontSize: 10, color: 'rgba(255,255,255,.4)' }}>vs</div>
        <Side side="b" />
      </div>
      {action && (
        <button
          onClick={action.fn}
          disabled={busy}
          style={{
            cursor: busy ? 'default' : 'pointer',
            border: 'none',
            borderRadius: 10,
            padding: 11,
            fontFamily: 'var(--hj-font-ui)',
            fontWeight: 700,
            fontSize: 13.5,
            color: action.fg,
            background: action.bg,
            opacity: busy ? 0.6 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
          }}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : action.icon}
          {action.label}
        </button>
      )}
    </div>
  );
};

// ── Stage card (Come On Down) ─────────────────────────────────────────
const StageCard: React.FC<{
  optInCount: number;
  contestants: HostContestant[];
  gateMode: GateMode;
  topN: number;
  drawCount: number;
  setDrawCount: (n: number) => void;
  drawing: boolean;
  drawError: string | null;
  settingsBusy: boolean;
  onDraw: () => void;
  onEject: (userId: string) => void;
  onSetGateMode: (m: GateMode) => void;
  onSetTopN: (n: number) => void;
}> = ({
  optInCount,
  contestants,
  gateMode,
  topN,
  drawCount,
  setDrawCount,
  drawing,
  drawError,
  settingsBusy,
  onDraw,
  onEject,
  onSetGateMode,
  onSetTopN,
}) => {
  const clamp = (n: number) => Math.max(1, Math.min(8, n));
  const drawLabel = gameModeLabel(gateMode, drawCount, topN);
  return (
    <div
      style={{
        border: '1px solid rgba(255,214,0,.28)',
        background: 'rgba(255,214,0,.05)',
        borderRadius: 14,
        padding: 15,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Sparkles size={15} color={GOLD} />
        <span className="font-hj-display" style={{ fontSize: 16, color: GOLD }}>Come On Down</span>
      </div>

      {/* opt-in count */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--hj-ink-2)' }}>
        <Users size={14} color={GREEN} />
        <b style={{ color: 'var(--hj-ink)' }}>{optInCount}</b> in the stage pool
      </div>

      {/* gate mode segment */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>Stage gate</label>
        <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,.28)', borderRadius: 9, padding: 3 }}>
          <Segment
            active={gateMode === 'strict'}
            disabled={settingsBusy}
            onClick={() => onSetGateMode('strict')}
            label="Strict top"
            activeBg={GOLD}
            activeFg="#150e20"
          />
          <Segment
            active={gateMode === 'top_n_draw'}
            disabled={settingsBusy}
            onClick={() => onSetGateMode('top_n_draw')}
            label="Draw from top N"
            activeBg={GOLD}
            activeFg="#150e20"
          />
        </div>
        {gateMode === 'top_n_draw' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>Random draw from the top</span>
            <input
              type="number"
              min={1}
              value={topN}
              disabled={settingsBusy}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (Number.isFinite(v) && v >= 1) onSetTopN(v);
              }}
              style={{
                width: 62,
                boxSizing: 'border-box',
                background: 'rgba(0,0,0,.28)',
                border: '1px solid rgba(255,255,255,.14)',
                borderRadius: 8,
                padding: '6px 8px',
                color: 'var(--hj-ink)',
                fontFamily: 'var(--hj-font-ui)',
                fontSize: 13,
                outline: 'none',
              }}
            />
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,.6)' }}>scorers</span>
          </div>
        )}
      </div>

      {/* count stepper */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>How many</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StepBtn onClick={() => setDrawCount(clamp(drawCount - 1))} disabled={drawCount <= 1}>
            <Minus size={14} />
          </StepBtn>
          <span className="font-hj-display tabular" style={{ fontSize: 20, width: 22, textAlign: 'center', color: GOLD }}>
            {drawCount}
          </span>
          <StepBtn onClick={() => setDrawCount(clamp(drawCount + 1))} disabled={drawCount >= 8}>
            <Plus size={14} />
          </StepBtn>
        </div>
      </div>

      {/* draw button */}
      <button
        onClick={onDraw}
        disabled={drawing}
        style={{
          width: '100%',
          cursor: drawing ? 'default' : 'pointer',
          border: 'none',
          borderRadius: 11,
          padding: 13,
          fontFamily: 'var(--hj-font-ui)',
          fontWeight: 700,
          fontSize: 14,
          color: '#150e20',
          background: 'linear-gradient(90deg,#ffe566,#ffd600)',
          opacity: drawing ? 0.6 : 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        {drawing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
        {drawLabel}
      </button>

      {drawError && (
        <div
          style={{
            fontSize: 12,
            lineHeight: 1.4,
            color: '#ffb9c6',
            background: 'rgba(255,77,106,.1)',
            border: '1px solid rgba(255,77,106,.35)',
            borderRadius: 10,
            padding: '9px 11px',
          }}
        >
          {drawError}
        </div>
      )}

      {/* drawn list */}
      {contestants.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>On stage · {contestants.length}</label>
          {contestants.map((c) => (
            <div
              key={c.userId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 10,
                background: 'rgba(140,198,63,.08)',
                border: '1px solid rgba(140,198,63,.3)',
              }}
            >
              <span className="font-hj-display tabular" style={{ fontSize: 14, width: 26, color: GREEN }}>
                {c.rank != null ? `#${c.rank}` : '—'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.userName}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.55)' }}>{c.totalPoints.toLocaleString()} pts</div>
              </div>
              <button
                onClick={() => onEject(c.userId)}
                title="Eject contestant"
                style={{
                  cursor: 'pointer',
                  border: '1px solid rgba(255,77,106,.4)',
                  background: 'rgba(255,77,106,.1)',
                  color: '#ff8199',
                  borderRadius: 7,
                  padding: '5px 6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const gameModeLabel = (gateMode: GateMode, count: number, topN: number): string =>
  gateMode === 'top_n_draw' ? `Draw ${count} from top ${topN}` : `Draw top ${count}`;

const formatUsd = (cents: number): string => {
  const d = cents / 100;
  return d % 1 === 0 ? `$${d.toLocaleString()}` : `$${d.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ── Sponsorship card ──────────────────────────────────────────────────
// Cash prizes are sponsor-funded. Host free-texts what a sponsor gets + a
// price → mint a shareable pay link. Paid rows show the sponsor + amount (this
// is the on-air prize provenance); the total funded shows at the top.
const SponsorshipCard: React.FC<{
  sponsorships: Sponsorship[];
  busy: boolean;
  onCreate: (offerText: string, amountCents: number) => void;
  onVoid: (sponsorshipId: string) => void;
}> = ({ sponsorships, busy, onCreate, onVoid }) => {
  const [offer, setOffer] = useState('');
  const [amount, setAmount] = useState(''); // whole dollars, as typed
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const totalPaidCents = sponsorships
    .filter((s) => s.status === 'paid')
    .reduce((sum, s) => sum + s.amountCents, 0);

  const copyLink = async (s: Sponsorship) => {
    try {
      await navigator.clipboard.writeText(s.payUrl);
      setCopiedId(s._id);
      setTimeout(() => setCopiedId((c) => (c === s._id ? null : c)), 1800);
    } catch {
      /* clipboard blocked */
    }
  };

  const submit = () => {
    const dollars = parseFloat(amount);
    if (!offer.trim()) {
      setFormError('Describe what the sponsor gets.');
      return;
    }
    if (!Number.isFinite(dollars) || dollars < 10) {
      setFormError('Amount must be at least $10.');
      return;
    }
    setFormError(null);
    onCreate(offer.trim(), Math.round(dollars * 100));
    setOffer('');
    setAmount('');
  };

  const chip: Record<Sponsorship['status'], { label: string; color: string }> = {
    pending: { label: 'Awaiting payment', color: GOLD },
    paid: { label: 'Funded', color: GREEN },
    void: { label: 'Withdrawn', color: 'rgba(255,255,255,.4)' },
  };

  const visible = sponsorships.filter((s) => s.status !== 'void');

  return (
    <div
      style={{
        border: '1px solid rgba(140,198,63,.28)',
        background: 'rgba(140,198,63,.05)',
        borderRadius: 14,
        padding: 15,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Gift size={15} color={GREEN} />
          <span className="font-hj-display" style={{ fontSize: 16, color: GREEN }}>
            Sponsorship
          </span>
        </div>
        {totalPaidCents > 0 && (
          <span className="font-hj-display tabular" style={{ fontSize: 17, color: 'var(--hj-ink)' }}>
            {formatUsd(totalPaidCents)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--hj-ink-2)' }}>
        Fund cash prizes with sponsors — never ticket money. Paid sponsors are credited on-air.
      </div>

      {/* list */}
      {visible.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visible.map((s) => {
            const c = chip[s.status];
            const paid = s.status === 'paid';
            return (
              <div
                key={s._id}
                style={{
                  borderRadius: 11,
                  padding: '10px 11px',
                  background: paid ? 'rgba(140,198,63,.08)' : 'rgba(255,255,255,.03)',
                  border: `1px solid ${paid ? 'rgba(140,198,63,.35)' : 'rgba(255,255,255,.09)'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ ...kicker, fontSize: 9, color: c.color }}>{c.label}</span>
                  <div style={{ flex: 1 }} />
                  <span className="font-hj-display tabular" style={{ fontSize: 14, color: paid ? GREEN : 'var(--hj-ink)' }}>
                    {formatUsd(s.amountCents)}
                  </span>
                  {s.status === 'pending' && (
                    <button
                      onClick={() => onVoid(s._id)}
                      title="Withdraw this offer"
                      style={{
                        cursor: 'pointer',
                        border: '1px solid rgba(255,77,106,.4)',
                        background: 'rgba(255,77,106,.1)',
                        color: '#ff8199',
                        borderRadius: 7,
                        padding: '4px 5px',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                {paid && s.sponsorName && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--hj-ink)' }}>{s.sponsorName}</div>
                )}
                <div style={{ fontSize: 12, lineHeight: 1.35, color: 'var(--hj-ink-2)' }}>{s.offerText}</div>
                {s.status === 'pending' && (
                  <button
                    onClick={() => copyLink(s)}
                    style={{
                      cursor: 'pointer',
                      border: '1px solid rgba(255,214,0,.4)',
                      background: 'rgba(255,214,0,.08)',
                      color: GOLD,
                      borderRadius: 9,
                      padding: '7px 10px',
                      fontFamily: 'var(--hj-font-ui)',
                      fontWeight: 700,
                      fontSize: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 7,
                    }}
                  >
                    {copiedId === s._id ? <Check size={13} /> : <Copy size={13} />}
                    {copiedId === s._id ? 'Link copied' : 'Copy pay link'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* create form */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: 12 }}>
        <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>New sponsorship</label>
        <textarea
          value={offer}
          onChange={(e) => setOffer(e.target.value)}
          maxLength={500}
          rows={2}
          placeholder="What the sponsor gets — banner ad, live read, cast a role…"
          style={{ ...inputStyle, width: '100%' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flex: 1,
              background: 'rgba(0,0,0,.28)',
              border: '1px solid rgba(255,255,255,.14)',
              borderRadius: 10,
              padding: '0 11px',
            }}
          >
            <span style={{ fontSize: 14, color: 'rgba(255,255,255,.5)' }}>$</span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              inputMode="decimal"
              placeholder="500"
              style={{
                flex: 1,
                width: '100%',
                boxSizing: 'border-box',
                border: 'none',
                background: 'transparent',
                color: 'var(--hj-ink)',
                fontFamily: 'var(--hj-font-ui)',
                fontWeight: 700,
                fontSize: 14,
                padding: '11px 0',
                outline: 'none',
              }}
            />
          </div>
          <button
            onClick={submit}
            disabled={busy}
            style={{
              cursor: busy ? 'default' : 'pointer',
              border: 'none',
              borderRadius: 10,
              padding: '11px 14px',
              fontFamily: 'var(--hj-font-ui)',
              fontWeight: 700,
              fontSize: 13,
              color: '#150e20',
              background: 'linear-gradient(90deg,#ffe566,#ffd600)',
              opacity: busy ? 0.6 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
            }}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Create link
          </button>
        </div>
        {formError && <div style={{ fontSize: 11.5, color: '#ffb9c6' }}>{formError}</div>}
      </div>
    </div>
  );
};

const StepBtn: React.FC<{ onClick: () => void; disabled?: boolean; children: React.ReactNode }> = ({
  onClick,
  disabled,
  children,
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      cursor: disabled ? 'default' : 'pointer',
      border: '1px solid rgba(255,255,255,.16)',
      background: 'rgba(255,255,255,.05)',
      color: 'var(--hj-ink)',
      borderRadius: 8,
      padding: '6px 8px',
      opacity: disabled ? 0.4 : 1,
      display: 'inline-flex',
      alignItems: 'center',
    }}
  >
    {children}
  </button>
);

// ── Shell ─────────────────────────────────────────────────────────────
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ minHeight: '100vh', background: PAGE_BG, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-ui)', padding: '24px' }}>
    <div
      style={{
        maxWidth: 1360,
        margin: '0 auto',
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,.08)',
        background: 'rgba(20,16,24,.5)',
      }}
    >
      {children}
    </div>
  </div>
);

// ── Status button ─────────────────────────────────────────────────────
const StatusButton: React.FC<{
  label: string;
  active: boolean;
  accent?: boolean;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ label, active, accent, danger, disabled, onClick }) => {
  const base = danger ? REC : accent ? GOLD : 'rgba(255,255,255,.7)';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'var(--hj-font-ui)',
        fontWeight: 700,
        fontSize: 12,
        padding: '8px 14px',
        borderRadius: 9,
        border: `1px solid ${active ? base : 'rgba(255,255,255,.16)'}`,
        color: active ? (accent ? '#150e20' : base) : 'rgba(255,255,255,.7)',
        background: active ? (accent ? GOLD : danger ? 'rgba(255,77,106,.14)' : 'rgba(255,255,255,.08)') : 'transparent',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {label}
    </button>
  );
};

// ── Queue row ─────────────────────────────────────────────────────────
const QueueRow: React.FC<{
  n: number;
  text: string;
  chip: { label: string; color: string };
  delivery: Delivery;
  isGate: boolean;
  selected: boolean;
  onSelect: () => void;
  onUp?: () => void;
  onDown?: () => void;
  onDelete?: () => void;
}> = ({ n, text, chip, delivery, isGate, selected, onSelect, onUp, onDown, onDelete }) => (
  <div
    style={{
      border: `1.5px solid ${selected ? 'rgba(255,214,0,.5)' : 'rgba(255,255,255,.09)'}`,
      background: selected ? 'rgba(255,214,0,.08)' : 'rgba(255,255,255,.02)',
      borderRadius: 12,
      padding: '11px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: 7,
    }}
  >
    <button
      onClick={onSelect}
      style={{ textAlign: 'left', cursor: 'pointer', background: 'transparent', border: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="font-hj-display" style={{ fontSize: 12, color: selected ? GOLD : 'rgba(255,255,255,.55)' }}>
          Q{n}
        </span>
        <span style={{ ...kicker, fontSize: 9, color: chip.color }}>{chip.label}</span>
        {isGate ? (
          <ShieldCheck size={12} color={PINK} />
        ) : delivery === 'host_read' ? (
          <Mic size={12} color="rgba(255,255,255,.5)" />
        ) : (
          <Monitor size={12} color={GREEN} />
        )}
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.35, color: 'var(--hj-ink-2)' }}>{text}</div>
    </button>
    {(onUp || onDown || onDelete) && (
      <div style={{ display: 'flex', gap: 6 }}>
        <IconBtn onClick={onUp} disabled={!onUp}>
          <ArrowUp size={13} />
        </IconBtn>
        <IconBtn onClick={onDown} disabled={!onDown}>
          <ArrowDown size={13} />
        </IconBtn>
        <div style={{ flex: 1 }} />
        <IconBtn onClick={onDelete} danger>
          <Trash2 size={13} />
        </IconBtn>
      </div>
    )}
  </div>
);

const IconBtn: React.FC<{ onClick?: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode }> = ({
  onClick,
  disabled,
  danger,
  children,
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      cursor: disabled ? 'default' : 'pointer',
      border: '1px solid rgba(255,255,255,.12)',
      background: 'rgba(255,255,255,.03)',
      color: danger ? PINK : 'rgba(255,255,255,.6)',
      borderRadius: 7,
      padding: '4px 7px',
      opacity: disabled ? 0.35 : 1,
      display: 'inline-flex',
      alignItems: 'center',
    }}
  >
    {children}
  </button>
);

// ── Live control (current question) ───────────────────────────────────
const LiveControl: React.FC<{
  q: HostQuestion;
  busy: boolean;
  onLock: () => void;
  onReveal: () => void;
  onNext: () => void;
  hasNext: boolean;
}> = ({ q, busy, onLock, onReveal, onNext, hasNext }) => {
  const hostRead = q.delivery === 'host_read';

  let primaryLabel: string;
  let primaryBg: string;
  let primaryFg: string;
  let primaryAction: (() => void) | null;
  let primaryDisabled = busy;
  if (q.state === 'open') {
    primaryLabel = 'Lock answers';
    primaryBg = 'linear-gradient(90deg,#ff4d6a,#f5236a)';
    primaryFg = '#fff';
    primaryAction = onLock;
  } else if (q.state === 'locked') {
    primaryLabel = 'Reveal to audience';
    primaryBg = 'linear-gradient(90deg,#8cc63f,#63a828)';
    primaryFg = '#08110c';
    primaryAction = onReveal;
  } else {
    // revealed
    primaryLabel = hasNext ? 'Next question →' : 'Show complete';
    primaryBg = 'linear-gradient(90deg,#ffe566,#ffd600)';
    primaryFg = '#150e20';
    primaryAction = hasNext ? onNext : null;
    primaryDisabled = !hasNext;
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ ...kicker, letterSpacing: '0.16em', color: 'rgba(255,255,255,.5)' }}>Question {q.order + 1}</span>
        <span style={{ ...kicker, color: stateChip[q.state].color }}>{stateChip[q.state].label}</span>
      </div>

      <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: 22 }}>
        <DeliveryLine delivery={q.delivery} isGate={q.isGate} />
        <div style={{ fontSize: 21, fontWeight: 700, lineHeight: 1.28, marginTop: 12, color: 'var(--hj-ink)' }}>{q.text}</div>
        <div style={{ fontSize: 11.5, lineHeight: 1.4, color: 'rgba(255,255,255,.55)', marginTop: 10 }}>
          {hostRead
            ? 'Text stays off all phones — you read it aloud; options-only appear when you Open.'
            : 'Shown on every phone. Safe for contestant rounds — the camera + clock are the anti-cheat.'}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {q.options.map((opt, i) => {
          const isCorrect = q.correctOption === i;
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 13,
                border: `1.5px solid ${isCorrect ? 'rgba(140,198,63,.4)' : 'rgba(255,255,255,.1)'}`,
                background: isCorrect ? 'rgba(140,198,63,.06)' : 'rgba(255,255,255,.03)',
                borderRadius: 13,
                padding: '13px 15px',
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 13,
                  background: isCorrect ? GREEN : 'rgba(255,255,255,.1)',
                  color: isCorrect ? '#08110c' : 'rgba(255,255,255,.85)',
                }}
              >
                {LETTERS[i]}
              </span>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: 'var(--hj-ink)' }}>{opt}</span>
              {isCorrect && <span style={{ ...kicker, color: GREEN }}>✓ answer</span>}
            </div>
          );
        })}
      </div>

      {/* answer count */}
      <div
        style={{
          background: 'rgba(255,255,255,.04)',
          border: '1px solid rgba(255,255,255,.08)',
          borderRadius: 13,
          padding: '13px 15px',
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>Answers in</div>
        <div className="font-hj-display tabular" style={{ fontSize: 26, color: 'var(--hj-ink)' }}>
          {q.answerCount}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
        <button
          onClick={() => primaryAction && primaryAction()}
          disabled={primaryDisabled}
          style={{
            flex: 1,
            cursor: primaryDisabled ? 'default' : 'pointer',
            border: 'none',
            borderRadius: 14,
            padding: 17,
            fontFamily: 'var(--hj-font-ui)',
            fontWeight: 700,
            fontSize: 16,
            color: primaryFg,
            background: primaryBg,
            opacity: primaryDisabled ? 0.55 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 9,
          }}
        >
          {busy ? (
            <Loader2 size={18} className="animate-spin" />
          ) : q.state === 'open' ? (
            <Lock size={18} />
          ) : q.state === 'locked' ? (
            <Eye size={18} />
          ) : null}
          {primaryLabel}
        </button>
      </div>
    </>
  );
};

// ── Draft editor (queued question authoring) ──────────────────────────
const DraftEditor: React.FC<{
  draft: Draft;
  status: ShowStatus;
  dirty: boolean;
  busy: boolean;
  saving: boolean;
  onPatch: (patch: Partial<Draft>) => void;
  onSave: () => void;
  onOpen: () => void;
}> = ({ draft, status, dirty, busy, saving, onPatch, onSave, onOpen }) => {
  const isVote = draft.correctOption == null;

  const setOption = (i: number, val: string) => {
    const options = draft.options.map((o, idx) => (idx === i ? val : o));
    onPatch({ options });
  };
  const addOption = () => {
    if (draft.options.length >= 4) return;
    onPatch({ options: [...draft.options, ''] });
  };
  const removeOption = (i: number) => {
    if (draft.options.length <= 2) return;
    const options = draft.options.filter((_, idx) => idx !== i);
    let correctOption = draft.correctOption;
    if (correctOption != null) {
      if (correctOption === i) correctOption = 0;
      else if (correctOption > i) correctOption -= 1;
    }
    onPatch({ options, correctOption });
  };

  const canOpen = !!draft._id && !dirty && status === 'live';
  const openHint = !draft._id || dirty ? 'Save the queue first' : status !== 'live' ? 'Go live to open' : '';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ ...kicker, letterSpacing: '0.16em', color: 'rgba(255,255,255,.5)' }}>
          {draft._id ? 'Edit question' : 'New question'}
        </span>
      </div>

      {/* Question text */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>Question</label>
        <textarea
          value={draft.text}
          onChange={(e) => onPatch({ text: e.target.value })}
          placeholder="Whose acting technique is built on the repetition exercise?"
          rows={2}
          style={inputStyle}
        />
      </div>

      {/* Delivery + type toggles */}
      <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>Delivery</label>
          <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,.28)', borderRadius: 9, padding: 3 }}>
            <Segment
              active={draft.delivery === 'host_read' || draft.isGate}
              disabled={draft.isGate}
              onClick={() => onPatch({ delivery: 'host_read' })}
              icon={<Mic size={13} />}
              label="Host-read"
              activeBg={PINK}
              activeFg="#fff"
            />
            <Segment
              active={draft.delivery === 'on_screen' && !draft.isGate}
              disabled={draft.isGate}
              onClick={() => onPatch({ delivery: 'on_screen' })}
              icon={<Monitor size={13} />}
              label="On-screen"
              activeBg={GREEN}
              activeFg="#08110c"
            />
            <Segment
              active={draft.delivery === 'both' && !draft.isGate}
              disabled={draft.isGate}
              onClick={() => onPatch({ delivery: 'both' })}
              icon={<Radio size={13} />}
              label="Both"
              activeBg={GOLD}
              activeFg="#1a1405"
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>Scoring</label>
          <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,.28)', borderRadius: 9, padding: 3 }}>
            <Segment
              active={!isVote}
              onClick={() => onPatch({ correctOption: draft.correctOption == null ? 0 : draft.correctOption })}
              label="Scored"
              activeBg={GOLD}
              activeFg="#150e20"
            />
            <Segment
              active={isVote}
              onClick={() => onPatch({ correctOption: null })}
              label="Vote"
              activeBg={GOLD}
              activeFg="#150e20"
            />
          </div>
        </div>
      </div>

      {draft.isGate && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            fontSize: 12,
            lineHeight: 1.4,
            color: 'var(--hj-ink-2)',
            background: 'var(--hj-pink-tint)',
            border: '1px solid rgba(242,134,201,.35)',
            borderRadius: 11,
            padding: '10px 12px',
          }}
        >
          <ShieldCheck size={15} color={PINK} style={{ flexShrink: 0, marginTop: 1 }} />
          Gate question — delivery is locked to host-read. Stage-gating rounds are forced host-read with aggressive speed
          decay so there's nothing on a phone to Google.
        </div>
      )}

      {/* Options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>
          Options {isVote ? '(vote — no correct answer)' : '(pick the correct one)'}
        </label>
        {draft.options.map((opt, i) => {
          const correct = draft.correctOption === i;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {!isVote && (
                <button
                  onClick={() => onPatch({ correctOption: i })}
                  title="Mark correct"
                  style={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: `2px solid ${correct ? GREEN : 'rgba(255,255,255,.2)'}`,
                    background: correct ? GREEN : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {correct && <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#08110c' }} />}
                </button>
              )}
              <span
                style={{
                  flexShrink: 0,
                  width: 24,
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: 13,
                  color: 'rgba(255,255,255,.6)',
                }}
              >
                {LETTERS[i]}
              </span>
              <input
                value={opt}
                onChange={(e) => setOption(i, e.target.value)}
                placeholder={`Option ${LETTERS[i]}`}
                style={{ ...inputStyle, flex: 1 }}
              />
              <IconBtn onClick={() => removeOption(i)} disabled={draft.options.length <= 2} danger>
                <Trash2 size={13} />
              </IconBtn>
            </div>
          );
        })}
        {draft.options.length < 4 && (
          <button
            onClick={addOption}
            style={{
              alignSelf: 'flex-start',
              border: '1px dashed rgba(255,255,255,.2)',
              background: 'transparent',
              color: 'var(--hj-ink-2)',
              fontFamily: 'var(--hj-font-ui)',
              fontWeight: 600,
              fontSize: 12.5,
              padding: '7px 12px',
              borderRadius: 9,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Plus size={13} /> Add option
          </button>
        )}
      </div>

      {/* Points / decay / gate */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <NumField
          label="Points"
          value={draft.points}
          min={0}
          onChange={(v) => onPatch({ points: v })}
        />
        <NumField
          label="Decay (sec)"
          value={draft.decaySeconds}
          min={1}
          onChange={(v) => onPatch({ decaySeconds: v })}
        />
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            fontSize: 13,
            color: 'var(--hj-ink)',
            paddingBottom: 6,
          }}
        >
          <input
            type="checkbox"
            checked={draft.isGate}
            onChange={(e) => onPatch({ isGate: e.target.checked, delivery: e.target.checked ? 'host_read' : draft.delivery })}
            style={{ width: 16, height: 16, accentColor: PINK }}
          />
          <ShieldCheck size={14} color={PINK} /> Stage gate
        </label>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, marginTop: 4, alignItems: 'center' }}>
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          style={{
            cursor: !dirty || saving ? 'default' : 'pointer',
            border: 'none',
            borderRadius: 13,
            padding: '14px 22px',
            fontFamily: 'var(--hj-font-ui)',
            fontWeight: 700,
            fontSize: 15,
            color: '#150e20',
            background: 'linear-gradient(90deg,#ffe566,#ffd600)',
            opacity: !dirty || saving ? 0.5 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : null}
          {dirty ? 'Save queue' : 'Saved'}
        </button>
        <button
          onClick={onOpen}
          disabled={!canOpen || busy}
          title={openHint}
          style={{
            cursor: !canOpen || busy ? 'default' : 'pointer',
            border: `1.5px solid ${canOpen ? 'rgba(255,214,0,.5)' : 'rgba(255,255,255,.14)'}`,
            background: canOpen ? 'rgba(255,214,0,.1)' : 'rgba(255,255,255,.03)',
            color: canOpen ? GOLD : 'rgba(255,255,255,.5)',
            borderRadius: 13,
            padding: '14px 22px',
            fontFamily: 'var(--hj-font-ui)',
            fontWeight: 700,
            fontSize: 15,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Play size={16} /> Open question
        </button>
        {openHint && <span style={{ fontSize: 12, color: 'rgba(255,255,255,.5)' }}>{openHint}</span>}
      </div>
    </>
  );
};

const DeliveryLine: React.FC<{ delivery: Delivery; isGate: boolean }> = ({ delivery, isGate }) => {
  const hostRead = delivery === 'host_read';
  const color = hostRead ? PINK : GREEN;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, ...kicker, color }}>
      {isGate ? <ShieldCheck size={14} /> : hostRead ? <Mic size={14} /> : <Monitor size={14} />}
      {hostRead ? 'Host-read · off every phone' : 'On-screen · contestant is on camera'}
    </div>
  );
};

const Segment: React.FC<{
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  label: string;
  activeBg: string;
  activeFg: string;
}> = ({ active, disabled, onClick, icon, label, activeBg, activeFg }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      cursor: disabled ? 'not-allowed' : 'pointer',
      border: 'none',
      borderRadius: 7,
      padding: '7px 13px',
      fontFamily: 'var(--hj-font-ui)',
      fontWeight: 600,
      fontSize: 12,
      color: active ? activeFg : 'rgba(255,255,255,.6)',
      background: active ? activeBg : 'transparent',
      opacity: disabled && !active ? 0.4 : 1,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    }}
  >
    {icon}
    {label}
  </button>
);

const NumField: React.FC<{ label: string; value: number; min?: number; onChange: (v: number) => void }> = ({
  label,
  value,
  min,
  onChange,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
    <label style={{ ...kicker, color: 'rgba(255,255,255,.5)' }}>{label}</label>
    <input
      type="number"
      value={value}
      min={min}
      onChange={(e) => {
        const v = parseInt(e.target.value, 10);
        onChange(Number.isNaN(v) ? min ?? 0 : v);
      }}
      style={{ ...inputStyle, width: 110 }}
    />
  </div>
);

const inputStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  background: 'rgba(0,0,0,.28)',
  border: '1px solid rgba(255,255,255,.14)',
  borderRadius: 10,
  padding: '11px 13px',
  color: 'var(--hj-ink)',
  fontFamily: 'var(--hj-font-ui)',
  fontSize: 14,
  outline: 'none',
  resize: 'vertical',
};

export default GameShowHostPage;
